import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { defineMessages, useIntl } from 'react-intl';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type {
  LineItemFragmentFragment,
  WishlistLineItemsFragmentFragment,
  CheckoutOrderableProduct,
} from '@packages/gql/generated/shopping/graphql';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type {
  GlycerinCartProduct,
  GlycerinWishListProduct,
} from '@packages/tracking/src/types/types';
import type {
  GTMEventGlycerinRemoveFromCart,
  GTMEventGlycerinRemoveFromWishList,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { dispatchButtonTracking } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { WishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistFragmentFragmentDoc';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import type { ButtonHTMLAttributes } from 'react';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import { buttonStylingTailwind, iconStylingTailwind } from './Actions.styles';
import { GraphqlDocuments } from './mappedModeToDocument';
import { useDeleteFromOrderableBasket } from '../../hooks/useOrderableBasketActions';

export interface BasketDeleteActionProps {
  mode: 'basket';
  lineItemId: LineItemFragmentFragment['id'];
  lineItemSku?: never;
  glycerinProduct: GlycerinCartProduct;
  overrideButtonClassName?: string;
  galleryView?: boolean;
}

export interface WishlistDeleteActionProps {
  mode: 'wishlist';
  lineItemSku: WishlistLineItemsFragmentFragment['sku'];
  lineItemId?: never;
  glycerinProduct: GlycerinWishListProduct;
  overrideButtonClassName?: string;
  galleryView?: boolean;
}

export interface CheckoutDeleteActionProps {
  mode: 'checkout';
  lineItemId: CheckoutOrderableProduct['lineItemId'];
  lineItemSku?: never;
  glycerinProduct: GlycerinCartProduct;
  overrideButtonClassName?: string;
  galleryView?: boolean;
}

export type DeleteActionProps =
  | BasketDeleteActionProps
  | WishlistDeleteActionProps
  | CheckoutDeleteActionProps;
const messages = defineMessages({
  buttonText: {
    id: 'DeleteAction.ButtonText',
    defaultMessage: 'Löschen',
    description: 'this will be displayed on the button',
  },
  successBasketMessage: {
    id: 'DeleteAction.Basket.SuccessMessage',
    defaultMessage: 'Ihr Artikel wurde erfolgreich aus dem Warenkorb entfernt',
  },
  successWishlistMessage: {
    id: 'DeleteAction.Wishlist.SuccessMessage',
    defaultMessage: 'Ihr Artikel wurde erfolgreich von Ihrem Merkzettel entfernt',
  },
  errorMessage: {
    id: 'DeleteAction.ErrorMessage',
    defaultMessage: 'Fehler beim Entfernen des Artikels',
  },
  ariaLabel: {
    id: 'DeleteAction.Wishlist.AreaLabel',
    defaultMessage: 'Löschen',
  },
});

export const DeleteAction: React.FC<
  DeleteActionProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = ({
  mode,
  glycerinProduct,
  lineItemId,
  lineItemSku,
  overrideButtonClassName,
  galleryView = false,
  onClick,
  ...props
}) => {
  const { formatMessage, locale } = useIntl();
  // a current limitation of typescript is that it cannot infer the corect type of the mutation result so useAuthMutation(GraphqlDocuments[mode]) won't work.
  const [basketState, removeFromBasket] = useAuthMutation(GraphqlDocuments.basket);
  const [wishListState, removeFromWishlist] = useAuthMutation(GraphqlDocuments.wishlist);
  const [checkoutState, removeFromOrderableBasket] = useDeleteFromOrderableBasket();

  const { addNotification } = useNotification();
  const dispatchGlycerin = useTracking();

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatchButtonTracking({
      eventDispatcher: dispatchGlycerin,
      type: 'button',
      clickTrackingProps: {
        category: 'Artikel Löschen',
        label: formatMessage(messages.buttonText),
      },
    });
    onClick?.(event);
    if (mode === 'basket' && !basketState.fetching && lineItemId) {
      removeFromBasket({ locale, lineItemId })
        .then((result) => {
          const removeFromBasketResult = result.data?.removeFromBasket;
          const basket = getFragmentData(BasketFragmentFragmentDoc, removeFromBasketResult);
          if (basket && basket.success) {
            addNotification({
              severity: 'success',
              message: formatMessage(messages.successBasketMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: {
                removedProducts: [glycerinProduct],
              },
            });
            getBridge().updateBasket();
          }
        })
        .catch(() => {
          // no need to grab the error variable currently we aren't displaying it
          addNotification({
            severity: 'error',
            message: formatMessage(messages.errorMessage),
            duration: 5000,
            onClose: () => {},
          });
        });
    }
    if (mode === 'wishlist' && !wishListState.fetching && lineItemSku) {
      removeFromWishlist({ locale, sku: lineItemSku })
        .then((result) => {
          if (result.error) throw new Error('error');
          const removeFromWishlistResult = result.data?.removeFromWishlist;
          const wishlist = getFragmentData(WishlistFragmentFragmentDoc, removeFromWishlistResult);
          if (wishlist) {
            dispatchGlycerin<GTMEventGlycerinRemoveFromWishList>({
              event: 'RemoveFromWishList',
              RemoveFromWishListData: {
                removedProducts: [glycerinProduct],
              },
            });
            addNotification({
              severity: 'success',
              message: formatMessage(messages.successWishlistMessage),
              duration: 5000,
              onClose: () => {},
            });
            getBridge().updateWishlist();
          }
        })
        .catch(() => {
          addNotification({
            severity: 'error',
            message: formatMessage(messages.errorMessage),
            duration: 5000,
            onClose: () => {},
          });
        });
    }
    if (mode === 'checkout' && !checkoutState.fetching && lineItemId) {
      removeFromOrderableBasket({ locale, pli: lineItemId })
        .then((result) => {
          const resultData = result.data?.deleteProductFromOrderableBasket;
          if (resultData?.__typename === 'CheckoutChangedOrderableBasket' && resultData.success) {
            addNotification({
              severity: 'success',
              message: formatMessage(messages.successBasketMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: { removedProducts: [glycerinProduct] },
            });
          }
        })
        .catch(() => {
          addNotification({
            severity: 'error',
            message: formatMessage(messages.errorMessage),
            duration: 5000,
            onClose: () => {},
          });
        });
    }
  };
  const isFetching = basketState.fetching || wishListState.fetching || checkoutState.fetching;
  const stateIcon = isFetching ? (
    <CircularProgress className={iconStylingTailwind} />
  ) : (
    <div className={`icon-emp-trash size-4 md:size-3 ${iconStylingTailwind}`} />
  );
  return (
    <Button
      className={`${buttonStylingTailwind} ${overrideButtonClassName || ''}`}
      onClick={onClickHandler}
      data-testid="delete-action"
      variant={galleryView ? 'icon-only' : 'icon-flat'}
      size="base"
      aria-label={formatMessage(messages.ariaLabel)}
      disabled={isFetching}
      {...props}
    >
      {stateIcon}
      {!galleryView && formatMessage(messages.buttonText)}
    </Button>
  );
};
